<template>
  <div v-if="card">
    <Card
      :card="card"
      layout="detail"
      @game-change="openGameChangePopup"
      @design-change="openDesignChangePopup"
    />
    <ExtraBonusLine
      v-if="extraBonus"
      :game="cardGame"
      :card="card"
      :extraBonus="extraBonus"
      :partner="card.partner"
    />
    <Benefits
      :key="card.currentGame"
      :selected-game="card.currentGame"
      :partner="card.partner"
      :cardType="card.cardType"
      :show="showBenefits"
      @toggle="toggleBenefits"
    />
    <AccrualsHistory
      :card="card"
      :accruals="cardAccruals"
      :meta="accrualsMeta"
      :loading="accrualsLoading"
      @show-more="fetchCardAccruals"
    />
    <Portal to="overlay" v-if="showChangeDialog">
      <BindingChangeDialog
        :current-game="card.currentGame"
        :card-code="card.cardCode"
        @close="showChangeDialog = false"
      />
    </Portal>

    <Portal to="overlay" v-if="showDesignChangeDialog">
      <DesignChangeDialog :card="card" @close="showDesignChangeDialog = false" />
    </Portal>
  </div>
</template>

<script>
import { Vue, Component, Prop, Watch } from 'vue-property-decorator'
import { namespace } from 'vuex-class'
import Card from '../components/Card'
import AccrualsHistory from '../components/AccrualsHistory'
import Benefits from '../components/Benefits'
import BindingChangeDialog from '../components/BindingChangeDialog'
import DesignChangeDialog from '../components/DesignChangeDialog'
import ExtraBonusLine from '../components/ExtraBonus/ExtraBonusLine'

const cards = namespace('cards')
const accruals = namespace('accruals')

const accrualsPerPage = 10

@Component({
  components: {
    Card,
    AccrualsHistory,
    Benefits,
    BindingChangeDialog,
    DesignChangeDialog,
    ExtraBonusLine
  }
})
class CardDetail extends Vue {
  /** @type {string} */
  @Prop({ type: String, required: true }) cardCode

  /** @type {Function} */
  @cards.Getter('cardByCode') getCardByCode

  @accruals.State accruals
  @accruals.State('meta') accrualsMeta
  @accruals.State('loading') accrualsLoading
  @accruals.Action fetchAccruals
  @accruals.Action fetchLastAccrualByCard

  showChangeDialog = false
  showDesignChangeDialog = false
  showBenefits = true

  created () {
    if (!this.card) {
      return this.$router.replace({ name: 'cards' })
    }
    this.fetchData()
  }

  get card () {
    return this.getCardByCode(this.cardCode)
  }

  get cardGame () {
    return this.card.currentGame || this.card.issuedGame
  }

  get cardAccruals () {
    return this.accruals[this.card.cardId] || []
  }

  get extraBonus () {
    return this.card.extraBonus
  }

  @Watch('card')
  fetchData () {
    if (this.card) {
      this.fetchLastAccrualByCard(this.card.cardId)
      this.fetchCardAccruals()
    }
  }

  /**
   * Fetch accruals with paging params for display history list
   * @param limit - count of records for fetch
   * @param offset - offset of records for fetch
   * @param mergeData - concat fetched data with exist or not
   */
  fetchCardAccruals ({ limit = accrualsPerPage, offset = 0, mergeData = false } = {}) {
    const options = {
      page: { limit, offset }
    }
    this.fetchAccruals({ cardId: this.card.cardId, options, mergeData })
  }

  openGameChangePopup () {
    this.$analytics.trackChangeCardGamePopupOpen({ screenId: this.$route.name, game: this.card.currentGame })
    this.showChangeDialog = true
  }

  openDesignChangePopup () {
    this.showDesignChangeDialog = true
  }

  toggleBenefits () {
    this.$analytics.trackAdditionalFuturesDetailPage({ screenId: this.$route.name, game: this.card.currentGame })
    this.showBenefits = !this.showBenefits
  }
}

export default CardDetail
</script>

<style lang="scss"></style>
